import { fromJS, Map, Set } from 'immutable';
import { dispatch, regEventDb, registerHandler } from 'nike-re-framejs';
import { defaultMiddlewares } from '@/common/util';
import { applyDelta, markVersion } from 'nike-ui/src/common/push/diff';
import { createAutoCashin, startCashin } from './api';
import { addToSet } from './utils';
import { cashinStatus } from '@/mytickets/constants/cashinStatus';
import {
    logCancelAutoCashinLimit,
    logCashinOffer,
    logCashinOfferUpdate,
    logCashinSubscriptionReset,
    logCashinTransactionResultConfirmed,
    logCashinTransactionStart,
    logClosePickSubmitBegin,
    logClosePickSubmitDone,
    logSetAutoCashinLimit
} from 'nike-ui/src/common/logging/cashin/log';
import { changeTicketusActiveMark } from '@/mytickets/active/handlers';
import { cancelAutoCashin } from '@/cashins/api';
import PopupNg from '@/ui/PopupNg';
import {
    closeClosingModeOnCurrentDevice,
    createCloseRequest,
    deleteActiveTransactionId,
    getEvalEventualTotalsNikeUi,
    saveIsLoading,
    setActiveTransactionId,
    startClosingTransaction
} from 'nike-ui/src/components/ticketus/handlers';
import { catcher } from '@/_http/utils';
import { CashinTransactionStatus } from 'nike-ui/src/types/cashin-closing';
import { loadCreateAnalysisBetIds } from '@/betbook/handlers';
import cashinusClient from '@/_http/axiosCashinusClient';
import { isProfileCashinPushIgnoreSequence } from 'nike-ui/src/profiles';
import {
    resetCashinSubscription,
    startCashinSubscription,
    stopCashinSubscription
} from 'nike-ui/src/components/ticketus/cashin/CashinPushSubscription';
import { trackDwhCashinEvent } from 'nike-ui/src/components/dwh-tracking/api';

//CASHIN ma v sebe pocitadlo - kolko prvkov zavolalo alebo odvolalo subscribe, vypne sa az ked sa vsetky unsubscribnu
regEventDb('ticketus/cashin-offer/subscribe', defaultMiddlewares, db => {
    const loggedIn = db.get('loggedIn');
    if (loggedIn) {
        startCashinSubscription();
    }
    return db;
});

regEventDb('ticketus/cashin-offer/unsubscribe', defaultMiddlewares, db => {
    stopCashinSubscription();
    return db;
});

regEventDb('ticketus/cashin-offer/snapshot', defaultMiddlewares, (db, [cashinSnapshots]) => {
    const cashins = cashinSnapshots.map(snapshot => {
        const cashin = fromJS(snapshot.payload)
            .update('betIds', Set)
            .update('betEventIds', Set);
        return markVersion(cashin, snapshot.version);
    });
    let cashinDb = Map();

    cashinDb = cashins.reduce((acc, cashin) => {
        logCashinOffer({ ticketId: cashin.get('ticketId'), offer: cashin });
        return acc.setIn(['tickets', cashin.get('ticketId')], cashin);
    }, cashinDb);

    cashinDb = cashins.reduce((acc, cashin) => {
        return cashin.get('betIds').reduce(addToSet(['bets'], cashin.get('ticketId')), acc);
    }, cashinDb);

    cashinDb = cashins.reduce((acc, cashin) => {
        return cashin.get('betEventIds').reduce(addToSet(['sportEvents'], cashin.get('ticketId')), acc);
    }, cashinDb);

    return db.setIn(['ticketus', 'cashin'], cashinDb);
});

export function onCashinOfferPushEvent(db, message) {
    const ticketId = message.entityId;
    const oldCashin = db.getIn(['ticketus', 'cashin', 'tickets', ticketId]);
    const oldTransactionStatus = oldCashin?.getIn(['activeTransaction', 'status']);

    const cashin = applyDelta(message.payload, oldCashin, message.version);
    const cashinTransactionStatus = cashin.getIn(['activeTransaction', 'status']);

    if (!isProfileCashinPushIgnoreSequence()) {
        const oldCashinPushSequence = oldCashin?.get('pushSequence');
        const cashinPushSequece = cashin.get('pushSequence');

        if (oldCashinPushSequence !== undefined && cashinPushSequece !== oldCashinPushSequence + 1) {
            logCashinSubscriptionReset({
                ticketId,
                offer: cashin,
                oldPushSequence: oldCashinPushSequence,
                pushJsCashinSubscriptionId: message.cashinSubscriptionId
            });
            resetCashinSubscription();
            return db;
        }
    }

    if (ticketId) {
        logCashinOfferUpdate({
            ticketId,
            offer: cashin,
            pushJsCashinSubscriptionId: message.cashinSubscriptionId
        });
        let newDb = db;
        if (
            oldTransactionStatus !== CashinTransactionStatus.Approved &&
            cashinTransactionStatus === CashinTransactionStatus.Approved
        ) {
            dispatch(loadCreateAnalysisBetIds(ticketId));
            newDb = closeClosingModeOnCurrentDevice({ db: newDb, ticketId, cashin });
        }
        return updateInCashinDb(newDb, message, cashin);
    }
    return db;
}

regEventDb('ticketus/cashin-offer/push', defaultMiddlewares, (db, [message]) => onCashinOfferPushEvent(db, message));

regEventDb('ticketus/cashin-offer/failure', defaultMiddlewares, db => db.setIn(['ticketus', 'cashin'], Map()));

const updateInCashinDb = (db, message, cashin) => {
    let cashinDb = db.getIn(['ticketus', 'cashin'], Map());
    cashinDb = cashinDb.setIn(['tickets', cashin.get('ticketId')], cashin);
    cashinDb = cashin.get('betIds', Set()).reduce(addToSet(['bets'], cashin.get('ticketId')), cashinDb);
    cashinDb = cashin.get('betEventIds', Set()).reduce(addToSet(['sportEvents'], cashin.get('ticketId')), cashinDb);

    return db.setIn(['ticketus', 'cashin'], cashinDb).update(db_ => {
        if (cashin.getIn(['activeTransaction', 'status']) === cashinStatus.Approved) {
            return changeTicketusActiveMark(db_);
        }

        return db_;
    });
};

regEventDb(
    'ticketus/cashin-transaction/start',
    defaultMiddlewares,
    (db, [ticketId, amount, acceptChanges, cashinOfferVersion, position]) => {
        const cashinDb = db.getIn(['ticketus', 'cashin'], Map());
        const offer = cashinDb.getIn(['tickets', ticketId]);
        logCashinTransactionStart({ ticketId, offer });
        trackDwhCashinEvent('manual_cashin', ticketId, offer.get('betType'), position);
        startCashin(ticketId, amount, acceptChanges, cashinOfferVersion)
            .then(response => {
                dispatch(setActiveTransactionId(response, ticketId));
            })
            .catch(catcher.handleMasterError);
        return db;
    }
);

registerHandler(
    'cashin/cashin-auto/create',
    defaultMiddlewares,
    (db, [ticketId, amount, cashinOfferVersion, position]) => {
        const eventName = 'set_automatic_cashin';
        const offer = db.getIn(['ticketus', 'cashin', 'tickets', ticketId]);
        trackDwhCashinEvent(eventName, ticketId, offer.get('betType'), position);
        createAutoCashin(ticketId, amount, cashinOfferVersion)
            .then(() => {
                PopupNg.dispatcher.close('cashin/auto-cashin-dialog');
            })
            .then(() =>
                PopupNg.dispatcher.show({
                    messages: 'autoCashin.createSuccess',
                    style: 'success'
                })
            )
            .catch(() => dispatch(db => db.setIn(['ticketus', 'cashin', 'autoCashinError'], 'CREATE_ERROR')));
        logSetAutoCashinLimit({ ticketId, automaticTransactionLimit: amount, offer });
        return db;
    }
);

registerHandler('cashin/cashin-auto/cancel', defaultMiddlewares, (db, [ticketId, cashinOfferVersion, position]) => {
    const eventName = 'reset_automatic_cashin';
    const offer = db.getIn(['ticketus', 'cashin', 'tickets', ticketId]);
    trackDwhCashinEvent(eventName, ticketId, offer.get('betType'), position);
    cancelAutoCashin(ticketId, cashinOfferVersion)
        .then(() => {
            PopupNg.dispatcher.close('cashin/auto-cashin-dialog');
        })
        .then(() =>
            PopupNg.dispatcher.show({
                messages: 'autoCashin.cancelSuccess',
                style: 'success'
            })
        )
        .catch(() => dispatch(db => db.setIn(['ticketus', 'cashin', 'autoCashinError'], 'DELETE_ERROR')));
    logCancelAutoCashinLimit({ ticketId, offer });

    return db;
});

registerHandler('cashin/cashin-auto/clearError', defaultMiddlewares, db =>
    db.deleteIn(['ticketus', 'cashin', 'autoCashinError'])
);

export const getOfferEventualTotals = (ticket, cashinOffer, cashinClosingBetIds) => db => {
    if (cashinClosingBetIds.length === 0) {
        return db.deleteIn(['ticketus', 'closing', cashinOffer.ticketId, 'eventualTotals']);
    }
    return getCashinOfferEventualTotalsMobile(db, ticket, cashinOffer, cashinClosingBetIds);
};

const isClosingTransactionActive = picks =>
    picks.every(
        pick =>
            pick.pickState === 'TRANSACTION_ACTIVE' || pick.pickState === 'FINISHED' || pick.pickState === 'UNAVAILABLE'
    );

const getCashinOfferEventualTotalsMobile = (db, ticket, cashinOffer, cashinClosingBetIds) => {
    if (isClosingTransactionActive(cashinOffer.picks)) return db;
    dispatch(saveIsLoading(true, cashinOffer.ticketId));
    getEvalEventualTotalsNikeUi(ticket, cashinOffer, cashinClosingBetIds, cashinusClient);
    return db;
};

export const closeCashinTicket = (checkBoxValue, requestedEventualWin, cashinOffer) => db => {
    const { ticketId, cashinClosingTransactionRequest } = createCloseRequest(db, checkBoxValue, requestedEventualWin);
    const cashinOfferVersion = cashinOffer.version;
    PopupNg.dispatcher.open('ticketus/cashin-closing', { ticketId }, 2);
    logClosePickSubmitBegin({ ticketId, offer: fromJS(cashinOffer) });
    cashinusClient
        .post(`cashin/v4/${ticketId}/${cashinOfferVersion}/closing-transaction/start`, cashinClosingTransactionRequest)
        .then(response => {
            dispatch(startClosingTransaction(response, ticketId));
            logClosePickSubmitDone({ ticketId, offer: fromJS(cashinOffer), error: undefined });
        })
        .catch(error => {
            dispatch(db => db.setIn(['ticketus', 'closing', 'result'], fromJS({ result: 'failure' })));
            logClosePickSubmitDone({ ticketId, offer: fromJS(cashinOffer), error });
        });
    return db;
};

export const closeCashinDialog = ({ offer, message }) => db => {
    // eslint-disable-next-line no-unused-vars
    const { ticketId } = offer.toJS();
    PopupNg.dispatcher.close('ticketus/cashin-closing');
    logCashinTransactionResultConfirmed({ ticketId, offer, message });
    return deleteActiveTransactionId(ticketId)(db).deleteIn(['ticketus', 'closing', 'transaction-started']);
};
